import { useSafeAreaInsetsStyle } from '@/ui/hooks/useSafeAreaInsetsStyle'
import { cn } from '@/ui/utils'
import { useScrollToTop } from '@react-navigation/native'
import { StatusBar } from 'expo-status-bar'
import { useRef } from 'react'
import { useEffect, useState } from 'react'
import { Keyboard, KeyboardAvoidingView, Platform, ScrollView, View } from 'react-native'
import type { ViewStyle } from 'react-native'
import { useAutoPreset } from './screen.hooks'
import type {
	AutoScreenProps,
	ScreenPreset,
	ScreenProps,
	ScrollScreenProps,
} from './screen.types'

import { screenStyles } from './screen.styles'

const isIos = Platform.OS === 'ios'

/**
 * @param {ScreenPreset?} preset - The preset to check.
 * @returns {boolean} - Whether the preset is non-scrolling.
 */
const isNonScrolling = (preset?: ScreenPreset) => {
	return !preset || preset === 'fixed'
}

const FixedScreen = (props: ScreenProps) => {
	const { contentContainerClassName, children, className } = props
	const { screenContainer, screenContent } = screenStyles()

	return (
		<View className={cn(screenContainer(), 'overflow-hidden', className)}>
			<View className={cn(screenContent(), contentContainerClassName)}>{children}</View>
		</View>
	)
}

const ScrollableScreen = (props: ScreenProps) => {
	const {
		children,
		keyboardShouldPersistTaps = 'handled',
		ScrollViewProps,
		className,
	} = props as ScrollScreenProps

	const ref = useRef<ScrollView>(null)

	const { screenContainer } = screenStyles()

	const { scrollEnabled, onContentSizeChange, onLayout } = useAutoPreset(
		props as AutoScreenProps
	)

	// Add native behavior of pressing the active tab to scroll to the top of the content
	// More info at: https://reactnavigation.org/docs/use-scroll-to-top/
	useScrollToTop(ref)

	return (
		<ScrollView
			{...{ keyboardShouldPersistTaps, scrollEnabled, ref }}
			{...ScrollViewProps}
			className={cn(screenContainer(), 'h-screen', className)}
			onLayout={(e) => {
				onLayout(e)
				ScrollViewProps?.onLayout?.(e)
			}}
			onContentSizeChange={(w: number, h: number) => {
				onContentSizeChange(w, h)
				ScrollViewProps?.onContentSizeChange?.(w, h)
			}}
			contentContainerStyle={[$innerStyle]}
		>
			{children}
		</ScrollView>
	)
}

/**
 * Represents a screen component that provides a consistent layout and behavior for different screen presets.
 * The `Screen` component can be used with different presets such as "fixed", "scroll", or "auto".
 * It handles safe area insets, status bar settings, keyboard avoiding behavior, and scrollability based on the preset.
 * @param {ScreenProps} props - The props for the `Screen` component.
 * @returns {JSX.Element} The rendered `Screen` component.
 */
export const Screen = (props: ScreenProps) => {
	const {
		KeyboardAvoidingViewProps,
		safeAreaEdges = [],
		StatusBarProps,
		className,
		preset,
	} = props

	const [keyboardStatus, setKeyboardStatus] = useState(1)
	useEffect(() => {
		const showSubscription = Keyboard.addListener('keyboardDidShow', () => {
			setKeyboardStatus(1)
		})
		const hideSubscription = Keyboard.addListener('keyboardDidHide', () => {
			setKeyboardStatus(0)
		})
		return () => {
			showSubscription.remove()
			hideSubscription.remove()
		}
	}, [])

	const $containerInsets = useSafeAreaInsetsStyle(safeAreaEdges)
	const { base, keyboardAvoidingView } = screenStyles()

	return (
		<View className={cn(base(), className)} style={[$containerInsets]}>
			{/* Dark text by default: every screen sits on the pink ground, and there is no dark mode. */}
			<StatusBar style="dark" {...StatusBarProps} />

			<KeyboardAvoidingView
				behavior={isIos ? 'padding' : 'height'}
				// Screens start at the window's top now (the tab bar floats over them), so nothing to offset.
				keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : keyboardStatus === 1 ? 64 : 0}
				{...KeyboardAvoidingViewProps}
				className={cn(keyboardAvoidingView())}
			>
				{isNonScrolling(preset) ? (
					<FixedScreen {...props} />
				) : (
					<ScrollableScreen {...props} />
				)}
			</KeyboardAvoidingView>
		</View>
	)
}

const $innerStyle: ViewStyle = {
	justifyContent: 'flex-start',
	alignItems: 'stretch',
	flexGrow: 1,
}
